import React, { useEffect, useMemo } from 'react';
import { CTC_COMPONENTS, detectCTCBand, computeCTC, resolveVisibility } from '../../../utils/ctcEngine';
import CTCBreakupTable from './CTCBreakupTable';

const formatINR = (amount) => `₹${Math.round(amount || 0).toLocaleString('en-IN')}`;

// Interactive band-aware CTC builder: annual CTC input, auto-detected band,
// per-component visibility overrides, and a live employee/employer breakup preview.
// The parent owns ctcInput/overrides state; the backend recomputes the same
// breakup server-side at offer-release time (this is a preview, not the source of truth).
const CTCBuilder = ({ ctcInput, overrides, onCtcInputChange, onOverridesChange, onBreakupChange }) => {
  const ctc = Number(ctcInput) || 0;
  const band = useMemo(() => detectCTCBand(ctc), [ctc]);
  const isVisible = (id) => resolveVisibility(id, band.id, overrides);
  const breakup = useMemo(
    () => computeCTC(ctc, isVisible),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctc, band.id, overrides]
  );

  useEffect(() => {
    onBreakupChange?.(ctc > 0 ? breakup : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakup, ctc]);

  const toggleableComponents = CTC_COMPONENTS.filter(c => !c.locked);

  const toggleComponent = (id) => {
    onOverridesChange({ ...overrides, [id]: !isVisible(id) });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm text-gray-700 mb-1">Annual CTC (₹)</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            value={ctcInput || ''}
            onChange={(e) => onCtcInputChange(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
            placeholder="e.g. 600000"
          />
          {ctc > 0 && (
            <span className="text-xs font-medium px-2 py-1 rounded bg-indigo-50 text-indigo-700 whitespace-nowrap">
              {band.label}
            </span>
          )}
        </div>
      </div>

      {ctc > 0 && (
        <>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">Component visibility</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {toggleableComponents.map(component => (
                <label key={component.id} className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={isVisible(component.id)}
                    onChange={() => toggleComponent(component.id)}
                  />
                  <span>{component.label}</span>
                  <span className="text-gray-400">({component.side === 'employee' ? 'Employee' : 'Employer'})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CTCBreakupTable side="employee" values={breakup.values} total={breakup.employeeTotal} title="Employee (Gross Salary)" />
            <CTCBreakupTable side="employer" values={breakup.values} total={breakup.employerTotal} title="Employer Contributions" />
          </div>

          <div className="flex justify-between text-sm font-semibold text-gray-900 px-1">
            <span>Grand Total (CTC)</span>
            <span>{formatINR(breakup.grandTotal)}</span>
          </div>

          {breakup.isOverAllocated && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              The enabled components alone total {formatINR(ctc + breakup.overAllocatedBy)}, which is{' '}
              {formatINR(breakup.overAllocatedBy)} more than the entered CTC. Increase the CTC or turn off some components before sending.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CTCBuilder;
